import React, {useState} from 'react'
import {EditorContent, useEditor} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import './WhatsAppEditor.css'

interface WhatsAppEditorProps {
  value: string
  onChange: (text: string) => void
  height?: number
  placeholder?: string
  variables?: string[]
  getVariableText?: (variable: string) => string
}

/**
 * WhatsApp Editor - A specialized Tiptap editor for WhatsApp messages
 * 
 * Supports only WhatsApp-compatible formatting:
 * - *Bold* -> <strong>
 * - _Italic_ -> <em>
 * - ~Strikethrough~ -> <s>
 * - ```Monospace``` -> <code>
 * - Emojis (native support)
 */
export const WhatsAppEditor: React.FC<WhatsAppEditorProps> = ({
  value,
  onChange,
  height = 300,
  placeholder = 'Escribe tu mensaje aquí...',
  variables = [],
  getVariableText,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showVariableDropdown, setShowVariableDropdown] = useState(false)

  // Debug: log variables to verify they're being passed
  console.log('WhatsAppEditor - Variables received:', variables, 'Count:', variables.length)

  // Common emojis for quick access
  const commonEmojis = [
    '😀', '😊', '😂', '🤣', '😍', '😘', '😎', '🤔', '😢', '😭',
    '😡', '🤯', '👍', '👎', '🙏', '💪', '👏', '🎉', '❤️', '🔥',
    '⭐', '✅', '❌', '⚠️', '💡', '📱', '📧', '💰', '🏠', '🚗'
  ]

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable features not supported by WhatsApp
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        // Keep only basic text formatting (defaults enabled)
      }),
    ],
    content: value,
    onUpdate: ({editor}) => {
      const html = editor.getHTML()
      // Convert HTML to WhatsApp format
      const whatsappText = convertHtmlToWhatsApp(html)
      onChange(whatsappText)
    },
    editorProps: {
      attributes: {
        class: 'whatsapp-editor-content',
        placeholder: placeholder,
      },
    },
  })

  React.useEffect(() => {
    if (editor && value !== convertHtmlToWhatsApp(editor.getHTML())) {
      // Convert WhatsApp format back to HTML for display
      const html = convertWhatsAppToHtml(value)
      editor.commands.setContent(html)
    }
  }, [value, editor])

  if (!editor) {
    return null
  }

  const insertEmoji = (emoji: string) => {
    editor.chain().focus().insertContent(emoji).run()
    setShowEmojiPicker(false)
  }

  const insertVariable = (variableKey: string) => {
    const variableText = getVariableText ? getVariableText(variableKey) : `{{${variableKey}}}`
    editor.chain().focus().insertContent(variableText).run()
    setShowVariableDropdown(false)
  }

  return (
    <div className='whatsapp-editor-wrapper'>
      {/* Toolbar */}
      <div className='whatsapp-toolbar'>
        <div className='d-flex align-items-center gap-2'>
          {/* WhatsApp Logo/Icon */}
          <div className='whatsapp-logo'>
            <i className='ki-outline ki-whatsapp fs-2 text-success'></i>
          </div>

          {/* Text Formatting */}
          <div className='btn-group btn-group-sm' role='group'>
            <button
              type='button'
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`btn btn-light-primary ${editor.isActive('bold') ? 'active' : ''}`}
              title='Bold (*texto*)'
            >
              <i className='bi bi-type-bold'></i>
            </button>
            <button
              type='button'
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`btn btn-light-primary ${editor.isActive('italic') ? 'active' : ''}`}
              title='Italic (_texto_)'
            >
              <i className='bi bi-type-italic'></i>
            </button>
            <button
              type='button'
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={`btn btn-light-primary ${editor.isActive('strike') ? 'active' : ''}`}
              title='Strikethrough (~texto~)'
            >
              <i className='bi bi-type-strikethrough'></i>
            </button>
            <button
              type='button'
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={`btn btn-light-primary ${editor.isActive('code') ? 'active' : ''}`}
              title='Monospace (```texto```)'
            >
              <i className='bi bi-code'></i>
            </button>
          </div>

          {/* Emoji Picker */}
          <div className='position-relative'>
            <button
              type='button'
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className='btn btn-sm btn-light-warning'
              title='Emojis'
            >
              😀
            </button>
            {showEmojiPicker && (
              <div className='emoji-picker-dropdown'>
                <div className='emoji-picker-header'>
                  <span className='text-muted small'>Emojis comunes</span>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon'
                    onClick={() => setShowEmojiPicker(false)}
                  >
                    <i className='bi bi-x'></i>
                  </button>
                </div>
                <div className='emoji-grid'>
                  {commonEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type='button'
                      className='emoji-button'
                      onClick={() => insertEmoji(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Variable Selector */}
          {variables.length > 0 && (
            <div className='position-relative ms-2'>
              <button
                type='button'
                onClick={() => setShowVariableDropdown(!showVariableDropdown)}
                className='btn btn-sm btn-light-primary'
                title='Insertar Variable'
              >
                <i className='bi bi-braces'></i>
                <span className='ms-1 d-none d-md-inline'>Variables</span>
              </button>
              {showVariableDropdown && (
                <div className='variable-picker-dropdown'>
                  <div className='variable-picker-header'>
                    <span className='text-muted small'>Variables disponibles</span>
                    <button
                      type='button'
                      className='btn btn-sm btn-icon'
                      onClick={() => setShowVariableDropdown(false)}
                    >
                      <i className='bi bi-x'></i>
                    </button>
                  </div>
                  <div className='variable-list'>
                    {variables.map((variable) => (
                      <button
                        key={variable}
                        type='button'
                        className='variable-item'
                        onClick={() => insertVariable(variable)}
                      >
                        <i className='bi bi-braces text-primary me-2'></i>
                        <code>{getVariableText ? getVariableText(variable) : `{{${variable}}}`}</code>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className='ms-auto'>
            <span className='badge badge-light-info'>
              <i className='bi bi-info-circle me-1'></i>
              Solo formatos de WhatsApp
            </span>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className='whatsapp-content' style={{minHeight: `${height}px`}}>
        <EditorContent editor={editor} />
      </div>

      {/* Format Legend */}
      <div className='whatsapp-legend'>
        <small className='text-muted'>
          <strong>Formatos:</strong> *negrita* • _itálica_ • ~tachado~ • ```monospace```
        </small>
      </div>
    </div>
  )
}

/**
 * Converts Tiptap HTML to WhatsApp formatting
 */
function convertHtmlToWhatsApp(html: string): string {
  let text = html
  
  // Remove paragraph tags
  text = text.replace(/<p>/g, '')
  text = text.replace(/<\/p>/g, '\n')
  
  // Convert formatting
  text = text.replace(/<strong>(.*?)<\/strong>/g, '*$1*')
  text = text.replace(/<b>(.*?)<\/b>/g, '*$1*')
  text = text.replace(/<em>(.*?)<\/em>/g, '_$1_')
  text = text.replace(/<i>(.*?)<\/i>/g, '_$1_')
  text = text.replace(/<s>(.*?)<\/s>/g, '~$1~')
  text = text.replace(/<strike>(.*?)<\/strike>/g, '~$1~')
  text = text.replace(/<code>(.*?)<\/code>/g, '```$1```')
  
  // Remove any remaining HTML tags
  text = text.replace(/<[^>]*>/g, '')
  
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ')
  text = text.replace(/&amp;/g, '&')
  text = text.replace(/&lt;/g, '<')
  text = text.replace(/&gt;/g, '>')
  text = text.replace(/&quot;/g, '"')
  
  // Clean up extra newlines
  text = text.replace(/\n\n+/g, '\n\n')
  text = text.trim()
  
  return text
}

/**
 * Converts WhatsApp formatting to Tiptap HTML
 */
function convertWhatsAppToHtml(text: string): string {
  if (!text) return '<p></p>'
  
  let html = text
  
  // Escape HTML special characters first
  html = html.replace(/&/g, '&amp;')
  html = html.replace(/</g, '&lt;')
  html = html.replace(/>/g, '&gt;')
  
  // Convert WhatsApp formatting to HTML
  // Bold: *text*
  html = html.replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
  
  // Italic: _text_
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>')
  
  // Strikethrough: ~text~
  html = html.replace(/~([^~]+)~/g, '<s>$1</s>')
  
  // Monospace: ```text```
  html = html.replace(/```([^`]+)```/g, '<code>$1</code>')
  
  // Convert newlines to paragraphs
  const paragraphs = html.split('\n').filter(p => p.trim())
  html = paragraphs.map(p => `<p>${p}</p>`).join('')
  
  return html || '<p></p>'
}
